
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import { PageBase } from 't4core';
import { ActivityPeriodSummary, DateValue, RentalInfo, RentalKPI, T4ListItem } from 't4core';


export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MMMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  styleUrls: ['./dashboard.css'],
  selector: 'u4f-dashboard',
  templateUrl: './dashboard.html',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DashboardComponent extends PageBase implements OnInit {
  public rentOuts: number = 0;
  public rentOutsPY: number = 0;
  public rentOutsDelta: number = 0;
  public netcostkickback: number = 0;
  public netcostkickbackPY: number = 0;
  public netcostkickbackDelta: number = 0;
  public availability: number = 0;
  public availabilityPY: number = 0;
  public availabilityDelta: number = 0;
  public revenue: number = 0;
  public revenuePY: number = 0;
  public revenueDelta: number = 0;
  public isLoading: boolean = false;

  public reportData: any[] = [];
  public reportDataPY: any[] = [];
  private _fromDate: Moment = moment().startOf('month');
  private _toDate: Moment = moment().endOf('month');
  public queryParameters: any = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
  public get fromDate(): Moment { return this._fromDate; }
  public set fromDate(value: Moment) {
    if (this._fromDate != value) {
      this._fromDate = value;
      this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
    }
  }
  public get toDate(): Moment { return this._toDate; }
  public set toDate(value: Moment) {
    if (this._toDate != value) {
      this._toDate = value;
      this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
    }
  } 

  public async updateParams() {
    this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };

    //Get api calls to get the data for now and previous year.
    this.isLoading = true;
    this.reportData = await this.Api.get<any[]>("U4FDashboard/GetU4FExecutiveSummary", { start: this.fromDate, end: this.toDate });
    const fromDatePY = this.fromDate.clone().year(this.fromDate.year() - 1);
    const toDatePY = this.toDate.clone().year(this.toDate.year() - 1);
    this.reportDataPY = await this.Api.get<any[]>("U4FDashboard/GetU4FExecutiveSummary", { start: fromDatePY, end: toDatePY });

    // Calculate the number of months
    const monthCount = this.getMonthCount(this.fromDate, this.toDate);
    const monthCountPY = this.getMonthCount(fromDatePY, toDatePY);

    // Set up rent-outs KPI values
    const trailers = this.reportData.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
    const trailersPY = this.reportDataPY.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
    this.rentOuts = trailers > 0 ? Math.round(this.reportData.reduce((sum, i) => sum + (i.AmountOfBookings || 0), 0) / trailers / monthCount) : 0;
    this.rentOutsPY = trailersPY > 0 ? Math.round(this.reportDataPY.reduce((sum, i) => sum + (i.AmountOfBookings || 0), 0) / trailersPY / monthCountPY) : 0;
    this.rentOutsDelta = (this.rentOutsPY < 0 && this.rentOuts < 0) ? (this.rentOuts / this.rentOutsPY - 1) * -1 : this.rentOutsPY > 0 ? this.rentOuts / this.rentOutsPY - 1 : 1;

    //set up netcostkickback kpi values.    
    this.netcostkickback = this.reportData?.length > 0 ? (this.reportData?.reduce((sum, i) => sum + (i.RevenueShare || 0), 0)) : 0;
    this.netcostkickbackPY = this.reportDataPY?.length > 0 ? (this.reportDataPY?.reduce((sum, i) => sum + (i.RevenueShare || 0), 0)) : 0;
    this.netcostkickbackDelta = (this.netcostkickbackPY < 0 && this.netcostkickback < 0) ? (this.netcostkickback / this.netcostkickbackPY - 1) * -1 : this.netcostkickbackPY > 0 ? this.netcostkickback / this.netcostkickbackPY - 1 : 1;

    //set up availability kpi values.    
    this.availability = this.reportData?.length > 0 ? Number((this.reportData?.reduce((sum, i) => sum + (i.AvailabilityOfTrailers || 0) / 100, 0) / this.reportData?.length).toFixed(2)) : 0;
    this.availabilityPY = this.reportDataPY?.length > 0 ? Number((this.reportDataPY?.reduce((sum, i) => sum + (i.AvailabilityOfTrailers || 0) / 100, 0) / this.reportDataPY?.length).toFixed(2)) : 0;
    this.availabilityDelta = (this.availabilityPY < 0 && this.availability < 0) ? (this.availability / this.availabilityPY - 1) * -1 : this.availabilityPY > 0 ? this.availability / this.availabilityPY - 1 : 1;

    // Set up average revenue per trailer KPI values
    this.revenue = trailers > 0 ? Math.round(this.reportData.reduce((sum, i) => sum + (i.IncomeFromBookingsExcVat || 0), 0) / trailers / monthCount) : 0;
    this.revenuePY = trailersPY > 0 ? Math.round(this.reportDataPY.reduce((sum, i) => sum + (i.IncomeFromBookingsExcVat || 0), 0) / trailersPY / monthCountPY) : 0;
    this.revenueDelta = (this.revenuePY < 0 && this.revenue < 0) ? (this.revenue / this.revenuePY - 1) * -1 : this.revenuePY > 0 ? this.revenue / this.revenuePY - 1 : 1;
    this.isLoading = false;
  }

  constructor(el: ElementRef) {
    super(el, "Use4FreeModule");
  }

  public async ngOnInit() {
    this.loadData();
  }

  public async loadData() {    
    this.fromDate = this.fromDate.clone();

    const today = moment();
    if (this.toDate.isSame(today, 'month') && this.toDate.year() === today.year()) {
      this.toDate = today;
    }
    await this.updateParams();
  }

  private getMonthCount(from: Moment, to: Moment): number {
    return Math.max(1, to.clone().startOf('month').diff(from.clone().startOf('month'), 'months') + 1);
  }

  public rentalLocationGroupChanged(rentalLocationGroup: T4ListItem) {
    this.loadData();
  }
}
